/**
 * iter-135 — Booking Held page.
 *
 * Customers land here after choosing Pay Later. Replaces the standard
 * "Booking Confirmed" success page (with confetti) so the copy matches
 * reality: their slot is held for 24h, payment is still required to
 * confirm. Deliberately calm — no confetti, no celebration.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Clock, Calendar, Users, CreditCard, AlertTriangle, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BookingHeld = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/bookings/${bookingId}`);
        if (cancelled) return;
        setBooking(res.data);
        // If the booking is already confirmed (deposit paid), forward to
        // the proper success page.
        if (res.data?.status === 'confirmed' || res.data?.deposit_paid) {
          navigate(`/booking/${bookingId}/success`, { replace: true });
          return;
        }
        // If cancelled, send to My Bookings (re-book section).
        if (res.data?.status === 'cancelled') {
          navigate('/my-bookings', { replace: true });
          return;
        }
      } catch (e) {
        console.error('Failed to load booking', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId, navigate]);

  // Tick every second to keep the countdown live
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFDF8]">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7A2E]"></div>
        </div>
      </div>
    );
  }

  const expiresAt = booking?.hold_expires_at ? new Date(booking.hold_expires_at) : null;
  const msLeft = expiresAt ? expiresAt.getTime() - now.getTime() : 0;
  const expired = msLeft <= 0;
  const hoursLeft = Math.max(0, Math.floor(msLeft / (1000 * 60 * 60)));
  const minutesLeft = Math.max(0, Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60)));

  const goPay = () => navigate(`/booking/${bookingId}/payment`);

  return (
    <div className="min-h-screen bg-[#FFFDF8]" data-testid="booking-held-page">
      <Header />
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl border-2 border-[#FF7A2E] p-6 sm:p-10 shadow-lg"
        >
          {/* Header banner */}
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 rounded-full bg-[#FFF4E6] flex items-center justify-center flex-shrink-0">
              <Clock className="w-8 h-8 text-[#FF7A2E]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>
                Booking Held — Complete Payment
              </h1>
              <p className="text-[#6B6B7A] mt-1">
                Your booking is <strong>tentatively held for 24 hours</strong>. Please complete your payment to confirm.
              </p>
            </div>
          </div>

          {/* Countdown banner */}
          <div className={`rounded-2xl p-4 mb-6 ${expired ? 'bg-red-50 border-2 border-red-300' : 'bg-[#FFF4E6] border-2 border-[#FFD166]'}`}
               data-testid="hold-countdown-banner">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`w-5 h-5 ${expired ? 'text-red-600' : 'text-[#B8860B]'}`} />
                <div>
                  <p className={`font-semibold ${expired ? 'text-red-700' : 'text-[#1E1E24]'}`}>
                    {expired ? 'This hold has expired' : 'Time remaining to confirm'}
                  </p>
                  {expiresAt && (
                    <p className="text-xs text-[#6B6B7A]">
                      Hold expires: <strong className="text-[#FF3B6B]">{expiresAt.toLocaleString()}</strong>
                    </p>
                  )}
                </div>
              </div>
              {!expired && (
                <div className="font-mono text-xl font-bold text-[#FF7A2E]" data-testid="hold-countdown-value">
                  {hoursLeft}h {String(minutesLeft).padStart(2, '0')}m
                </div>
              )}
            </div>
          </div>

          {/* Party summary card */}
          <div className="bg-[#FFFDF8] rounded-2xl border border-[#E8E5DF] p-5 mb-6" data-testid="held-party-summary">
            <p className="text-xs uppercase tracking-wider text-[#6B6B7A] font-semibold mb-3">Party details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#FF3B6B]" />
                <span className="text-[#1E1E24]"><strong>{booking?.child_name}</strong></span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#6366F1]" />
                <span className="text-[#1E1E24]">{booking?.guest_count} guests</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#06D6A0]" />
                <span className="text-[#1E1E24]">{booking?.date && new Date(booking.date).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#FF7A2E]" />
                <span className="text-[#1E1E24]">{booking?.time_slot === '10:00' ? '10 AM – 12 PM' : booking?.time_slot === '13:00' ? '1 PM – 3 PM' : '4 PM – 6 PM'}</span>
              </div>
              <div className="col-span-2 pt-3 mt-1 border-t border-[#E8E5DF] flex items-center justify-between">
                <span className="text-[#6B6B7A]">{booking?.package_name}</span>
                <span className="font-bold text-lg text-[#1E1E24]">${(booking?.total || 0).toFixed(2)} CAD</span>
              </div>
            </div>
          </div>

          {/* CTA — Pay Now */}
          <Button
            onClick={goPay}
            disabled={expired}
            className="w-full bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full py-6 text-lg font-semibold disabled:opacity-40"
            data-testid="held-pay-now-btn"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            {expired ? 'Hold expired — please re-book' : 'Pay Now to Confirm'}
          </Button>

          <p className="text-xs text-[#6B6B7A] text-center mt-3">
            Pay at least <strong>50% deposit</strong> or the full amount to confirm your booking.
          </p>

          {/* Email reassurance */}
          <div className="bg-[#FFFDF8] rounded-xl border border-[#E8E5DF] p-4 mt-6 flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#FF3B6B] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#6B6B7A]">
              <p>We've emailed your payment link to <strong className="text-[#1E1E24]">{booking?.customer_email}</strong>.</p>
              <p className="mt-1">
                Reference: <span className="font-mono text-[#FF3B6B]">{booking?.booking_reference || booking?.booking_id}</span>
              </p>
            </div>
          </div>

          <p className="text-xs text-[#6B6B7A] text-center mt-6">
            If you don't pay before the hold expires, your slot will be released and your booking auto-cancelled. You'll be able to re-book if the slot is still available.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default BookingHeld;
